// src/components/kuestion/solo/SoloShell.jsx
//
// The Solo frame (contract v3 §1.1): own banks, templates, sittings and
// progress behind the icon rail. No organisation, no students and no
// assignment concepts anywhere in the navigation.
import React from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import AppRail from '../shared/AppRail';
import { TourProvider, useTour } from '../shared/Tour';
import { PreferencesProvider, usePreferences } from '../shared/PreferencesContext';
import AccountMenu from '../shared/AccountMenu';
import AppNoticeboard from '../shared/AppNoticeboard';
import { GridIcon, ClockIcon, FileIcon, ChartIcon, CardIcon } from '../shared/Icons';
import { kq, kqRel } from '../../../config/routes';

export const SOLO_NAV = [
  { key: 'home', tour: 'solo-dash', to: '', label: 'Dashboard', icon: <GridIcon />, match: (p) => p === '' },
  { key: 'exam', to: 'exam', label: 'Sit a mock', icon: <ClockIcon />, match: (p) => p.startsWith('exam') },
  { key: 'banks', tour: 'solo-banks', to: 'banks', label: 'Banks', title: 'Banks & templates', icon: <FileIcon />, match: (p) => p.startsWith('banks') || p.startsWith('templates') },
  { key: 'progress', tour: 'solo-progress', to: 'progress', label: 'Progress', icon: <ChartIcon />, match: (p) => p.startsWith('progress') },
  { key: 'billing', tour: 'solo-billing', to: 'billing', label: 'Billing', icon: <CardIcon />, match: (p) => p.startsWith('billing') },
];

const crumbFor = (p) => {
  if (p === '') return 'Dashboard';
  if (p === 'exam') return 'Sit a mock';
  if (p.startsWith('exam/')) return 'Sitting';
  if (p === 'banks') return 'Banks & templates';
  if (p === 'banks/manage') return 'Banks & templates · manage data';
  if (p === 'templates') return 'Banks & templates · exam templates';
  if (p === 'templates/build') return 'Banks & templates · build a template';
  if (p === 'progress') return 'Progress';
  if (p === 'billing') return 'Billing';
  return '';
};

function Rail(props) {
  const tour = useTour();
  return <AppRail {...props} onGo={(n) => { props.onGo(n); if (tour) tour.visit(n.key); }} />;
}

function Page() {
  const p = usePreferences();
  return <Outlet key={p ? p.version : 0} />;
}

export default function SoloShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const rel = kqRel(location.pathname);
  const active = (SOLO_NAV.find((n) => n.match(rel)) || SOLO_NAV[0]).key;
  const name = user?.firstName ? `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}` : (user?.username || 'You');
  const initial = (name[0] || 'U').toUpperCase();

  // The runner owns its own header while a sitting is in progress.
  const inSitting = /^exam\/.+/.test(rel);

  const handleLogout = async () => {
    await logout();
    navigate(kq('login'));
  };

  return (
    // Never over a sitting: the runner owns the screen and the clock is running.
    <PreferencesProvider token={user?.token} onInvalidToken={handleLogout}>
    <TourProvider mode="solo" userId={user?.username} firstName={user?.firstName} enabled={!inSitting}>
    <div className="kqc">
      <div className={`kqc-app${inSitting ? ' is-sitting' : ''}`}>
        {!inSitting && (
          <Rail items={SOLO_NAV} active={active} onGo={(n) => navigate(n.to === '' ? kq() : kq(`${n.to}`))} onLogout={handleLogout} />
        )}
        <div className="kqc-main">
          {!inSitting && (
            <header className="kqc-header">
              <div style={{ minWidth: 0 }}>
                <div className="kqc-header__org">
                  <span>Kuestion</span>
                  <span className="kqc-header__prefix">Solo &middot; permanent</span>
                </div>
                <div className="kqc-header__crumb">{crumbFor(rel)}</div>
              </div>
              <span className="kqc-header__spacer" />
              <AppNoticeboard />
              <AccountMenu name={name} role="Studying alone" initial={initial} onLogout={handleLogout} note="Your mock times and streaks are counted against this zone." />
            </header>
          )}
          <Page />
        </div>
      </div>
    </div>
    </TourProvider>
    </PreferencesProvider>
  );
}
